import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Eye,
  EyeOff,
  ChevronLeft,
  Mail,
  Lock,
  User,
  Verified,
  Check,
  MapPin,
  HardDriveUpload,
  Calendar,
  // Phone, // Commented out - phone login disabled
  UserCircle,
  Loader2,
  CheckCircle,
} from "lucide-react";
import "./authStyles.css";
import bgi from "../assets/Apricverseuse.jpeg";

export default function AuthPage() {
  const {
    register,
    verifyEmailOrPhone,
    resendVerification,
    updateFarmDetails,
    updateExpertDetails,
    getProduce,
    storeFarmProduce,
    login
  } = useAuth();
  const navigate = useNavigate();

  const getDashboardPath = (userRole) => {
    if (!userRole) return '/farmer-dashboard';
    const role = userRole.toLowerCase();
    switch (role) {
      case 'expert':
        return '/expert-dashboard';
      case 'retailer':
        return '/retailer-dashboard';
      case 'farmer':
      default:
        return '/farmer-dashboard';
    }
  };

  // Form states
  const [currentStep, setCurrentStep] = useState("userType");
  const [userType, setUserType] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  // const [usePhoneNumber, setUsePhoneNumber] = useState(false); // Commented out - using email only for now
  const usePhoneNumber = false; // Always false - email only
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);
  const [produceList, setProduceList] = useState([]);
  const [selectedProduce, setSelectedProduce] = useState([]);
  const [selectedExpertiseAreas, setSelectedExpertiseAreas] = useState([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [showFinalSuccess, setShowFinalSuccess] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    user_name: "",
    email: "",
    // phone: "", // Commented out - using email only for now
    password: "",
    password_confirm: "",
    otp: "",
    farm_name: "",
    location: "",
    years_of_experience: "",
    expertise_document: null,
    extension_agent: false, // Extension agent field for experts
  });

  // Clear error and success when step changes
  useEffect(() => {
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);
  }, [currentStep]);

  // Load produce list when needed
  useEffect(() => {
    if (currentStep === "produce" && userType === "Farmer") {
      loadProduceList();
    }
  }, [currentStep, userType]);

  // Handle resend cooldown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const loadProduceList = async () => {
    try {
      const result = await getProduce();
      if (result.success) {
        setProduceList(result.data || []);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError("Failed to load produce list");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    // Update form data
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: files ? files[0] : value
      };

      // Real-time password match validation
      if (name === 'password' || name === 'password_confirm') {
        const currentPassword = name === 'password' ? value : prev.password;
        const currentPasswordConfirm = name === 'password_confirm' ? value : prev.password_confirm;

        // Only validate if both fields have values
        if (currentPassword && currentPasswordConfirm) {
          if (currentPassword !== currentPasswordConfirm) {
            setFieldErrors(prevErrors => ({
              ...prevErrors,
              password_confirm: 'Passwords do not match'
            }));
          } else {
            // Clear error if passwords match
            setFieldErrors(prevErrors => ({
              ...prevErrors,
              password_confirm: null
            }));
          }
        }
      }

      return updatedData;
    });

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Helper function to parse error messages and map to fields
  const parseErrorToFields = (errorMessage) => {
    const fieldMap = {
      'email': ['email', 'Email', 'EMAIL', 'email address', 'Email address'],
      'phone': ['phone', 'Phone', 'PHONE', 'phone number', 'Phone number'],
      'password': ['password', 'Password', 'PASSWORD'],
      'password_confirm': ['password confirm', 'Password confirm', 'confirm password', 'password confirmation', 'Password confirmation'],
      'first_name': ['first name', 'First name', 'first_name'],
      'last_name': ['last name', 'Last name', 'last_name'],
      'user_name': ['username', 'Username', 'user_name', 'user name'],
      'otp': ['otp', 'OTP', 'code', 'verification code', 'Verification code'],
      'farm_name': ['farm name', 'Farm name', 'farm_name'],
      'location': ['location', 'Location'],
      'years_of_experience': ['experience', 'years of experience', 'years_of_experience'],
      'expertise_document': ['document', 'Document', 'expertise document', 'Document of Expertise', 'certification', 'certificate']
    };

    const errors = {};
    const lowerMessage = errorMessage.toLowerCase();

    // First, try to split the message by common separators (comma, period, semicolon)
    const messageParts = errorMessage.split(/[,;.]/).map(part => part.trim()).filter(part => part.length > 0);

    // If we have multiple parts, try to map each part to a field
    if (messageParts.length > 1) {
      let foundFieldErrors = false;

      for (const part of messageParts) {
        const lowerPart = part.toLowerCase();

        // Check each field for this message part
        for (const [field, keywords] of Object.entries(fieldMap)) {
          for (const keyword of keywords) {
            if (lowerPart.includes(keyword.toLowerCase())) {
              // Create a specific error message for this field
              if (lowerPart.includes('already') && (lowerPart.includes('taken') || lowerPart.includes('exists'))) {
                errors[field] = `This ${keywords[0].toLowerCase()} is already taken`;
              } else if (lowerPart.includes('required')) {
                errors[field] = `${keywords[0]} is required`;
              } else if (lowerPart.includes('invalid')) {
                errors[field] = `Please enter a valid ${keywords[0].toLowerCase()}`;
              } else {
                errors[field] = part.trim();
              }
              foundFieldErrors = true;
              break;
            }
          }
          if (errors[field]) break; // Stop checking other keywords for this field
        }
      }

      if (foundFieldErrors) {
        return errors;
      }
    }

    // If no multiple parts found, check the whole message for field-specific errors
    for (const [field, keywords] of Object.entries(fieldMap)) {
      for (const keyword of keywords) {
        if (lowerMessage.includes(keyword.toLowerCase())) {
          // Create a more specific error message for this field
          if (lowerMessage.includes('already') && (lowerMessage.includes('taken') || lowerMessage.includes('exists'))) {
            errors[field] = `This ${keywords[0].toLowerCase()} is already taken`;
          } else if (lowerMessage.includes('required')) {
            errors[field] = `${keywords[0]} is required`;
          } else if (lowerMessage.includes('invalid')) {
            errors[field] = `Please enter a valid ${keywords[0].toLowerCase()}`;
          } else {
            errors[field] = errorMessage;
          }

          // For single field errors, return immediately
          if (messageParts.length === 1) {
            return errors;
          }
        }
      }
    }

    // If we found any field-specific errors, return them
    if (Object.keys(errors).length > 0) {
      return errors;
    }

    // If no specific field is identified, return empty object (will show global error)
    return {};
  };

  // Helper function to handle API errors that might be structured or string-based
  const handleApiError = (apiResult) => {
    // If API returns structured validation errors
    if (apiResult.errors && typeof apiResult.errors === 'object') {
      const mappedErrors = {};

      // Map API field names to our form field names
      const fieldMapping = {
        'first_name': 'first_name',
        'last_name': 'last_name',
        'user_name': 'user_name',
        'email': 'email',
        'phone': 'phone',
        'password': 'password',
        'otp': 'otp',
        'farm_name': 'farm_name',
        'location': 'location',
        'years_of_experience': 'years_of_experience',
        'document': 'expertise_document',
        'expertise_document': 'expertise_document',
        'areas_of_expertise': 'areas_of_expertise',
        'areas_of_expertise[]': 'areas_of_expertise',
      };

      for (const [apiField, message] of Object.entries(apiResult.errors)) {
        const formField = fieldMapping[apiField] || apiField;
        const errorMessage = Array.isArray(message) ? message[0] : message;
        mappedErrors[formField] = errorMessage;
      }

      if (Object.keys(mappedErrors).length > 0) {
        setFieldErrors(mappedErrors);
        setError(null);
        return;
      }
    }

    // Fall back to parsing the main message
    handleError(apiResult.message || apiResult);
  };

  // Helper function to set field-specific or global error
  const handleError = (errorMessage) => {
    const fieldErrors = parseErrorToFields(errorMessage);

    if (Object.keys(fieldErrors).length > 0) {
      setFieldErrors(fieldErrors);
      setError(null);
    } else {
      setError(errorMessage);
      setFieldErrors({});
    }
  };

  const handleUserTypeSelect = (type) => {
    setUserType(type);
    setCurrentStep("basicInfo");
  };

  const handleExpertiseToggle = (area) => {
    setSelectedExpertiseAreas(prev =>
      prev.includes(area)
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const handleProduceToggle = (produceId) => {
    setSelectedProduce(prev =>
      prev.includes(produceId)
        ? prev.filter(id => id !== produceId)
        : [...prev, produceId]
    );
  };

  const showSuccessAlert = (message) => {
    setSuccessMessage(message);

    // Check if this is the final success message
    if (message.includes("Registration completed! Welcome to your farm management platform!")) {
      setShowFinalSuccess(true);
      // Don't auto-clear the final success message
      return;
    }

    // Auto-clear success message after 3 seconds for other messages
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);

    try {
      switch (currentStep) {
        case "basicInfo":
          await handleBasicInfoSubmit();
          break;
        case "verification":
          await handleVerificationSubmit();
          break;
        case "farmDetails":
          await handleFarmDetailsSubmit();
          break;
        case "expertDetails":
          await handleExpertDetailsSubmit();
          break;
        case "produce":
          await handleProduceSubmit();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Submission error:", error);
      handleError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBasicInfoSubmit = async () => {
    // Validate password confirmation
    if (formData.password !== formData.password_confirm) {
      handleError("Passwords do not match");
      setFieldErrors(prev => ({ ...prev, password_confirm: 'Passwords do not match' }));
      return;
    }

    // Validate password is not empty
    if (!formData.password || formData.password.trim() === '') {
      handleError("Password is required");
      setFieldErrors(prev => ({ ...prev, password: 'Password is required' }));
      return;
    }

    const registrationData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      user_name: formData.user_name,
      password: formData.password,
      role: userType,
      verification_type: "email", // Always email - phone login commented out
      email: formData.email // Always use email
    };

    // Debug logging
    console.log('Registration attempt:', {
      ...registrationData,
      password: '[HIDDEN]'
    });

    const result = await register(registrationData);
    if (result.status) {
      // Store user_id from registration response for use in verification
      // Handle different response structures
      const userId = result.data?.user?.id || result.data?.id || result.data?.user_id;
      if (userId) {
        localStorage.setItem('tempUserId', userId);
        showSuccessAlert(result.message || "Registration successful! Please check your email for verification code.");
        setCurrentStep("verification");
      } else {
        console.error('Registration response structure:', result);
        handleError("Registration unsuccessful. Please try again.");
      }
    } else {
      console.log('Registration failed:', result);
      // Show the actual error message from API
      if (result.message) {
        handleApiError(result);
      } else {
        handleError(result.message || 'Registration unsuccessful');
      }
    }
  };

  const handleVerificationSubmit = async () => {
    // Validate OTP format before sending
    const otpValue = formData.otp.trim();

    if (!otpValue || otpValue.length !== 6 || !/^\d{6}$/.test(otpValue)) {
      handleError("Please enter a valid 6-digit verification code");
      return;
    }

    // Debug logging
    console.log('Verification attempt:', {
      otp: otpValue,
      otpAsNumber: parseInt(otpValue, 10),
      otpType: typeof otpValue,
      otpLength: otpValue.length,
      userType: userType,
      verificationType: 'email' // Always email - phone login commented out
    });

    // Get email from formData and pass it with OTP
    const email = formData.email;
    if (!email) {
      handleError("Email not found. Please try registering again.");
      return;
    }

    // Send OTP and email as expected by Laravel API
    const result = await verifyEmailOrPhone(otpValue, email);
    if (result.status) {
      showSuccessAlert(result.message || "Verification successful!");

      if (userType === "Farmer") {
        setCurrentStep("farmDetails");
      } else if (userType === "Expert") {
        setCurrentStep("expertDetails");
      } else {
        // Retailer - complete registration and login
        await completeRegistrationAndLogin();
      }
    } else {
      console.log('Verification failed:', result);
      handleApiError(result);
    }
  };

  const handleFarmDetailsSubmit = async () => {
    const userId = localStorage.getItem('tempUserId');
    if (!userId) {
      handleError("User ID not found. Please try registering again.");
      return;
    }

    const farmData = {
      user_id: userId,
      farm_name: formData.farm_name,
      location: formData.location
    };

    const result = await updateFarmDetails(farmData);
    if (result.status) {
      showSuccessAlert(result.message || "Farm details saved successfully!");
      setCurrentStep("produce");
    } else {
      handleApiError(result);
    }
  };

  const handleExpertDetailsSubmit = async () => {
    // Validate areas of expertise
    if (selectedExpertiseAreas.length === 0) {
      setFieldErrors(prev => ({ ...prev, areas_of_expertise: 'Please select at least one area of expertise' }));
      return;
    }

    // Validate years of experience
    const yearsValue = parseInt(formData.years_of_experience, 10);
    if (!formData.years_of_experience || isNaN(yearsValue) || yearsValue < 1) {
      setFieldErrors(prev => ({ ...prev, years_of_experience: 'Please enter valid years of experience (minimum 1)' }));
      return;
    }

    // Validate document is uploaded
    if (!formData.expertise_document) {
      setFieldErrors(prev => ({ ...prev, expertise_document: 'Document of expertise is required' }));
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const fileExtension = formData.expertise_document.name.split('.').pop().toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx'];

    if (!allowedExtensions.includes(fileExtension) && !allowedTypes.includes(formData.expertise_document.type)) {
      setFieldErrors(prev => ({ ...prev, expertise_document: 'Invalid file type. Please upload a PDF, DOC, or DOCX file' }));
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (formData.expertise_document.size > maxSize) {
      setFieldErrors(prev => ({ ...prev, expertise_document: 'File size exceeds 10MB limit' }));
      return;
    }

    const userId = localStorage.getItem('tempUserId');
    if (!userId) {
      handleError("User ID not found. Please try registering again.");
      return;
    }

    // Create FormData to send file
    const expertData = new FormData();
    expertData.append('user_id', userId);
    expertData.append('email', formData.email);
    // Send each area as a separate entry so Laravel receives a proper array (no JSON decode needed)
    selectedExpertiseAreas.forEach(area => {
      expertData.append('areas_of_expertise[]', area);
    });
    expertData.append('years_of_experience', yearsValue);
    expertData.append('extension_agent', formData.extension_agent ? '1' : '0');
    expertData.append('document', formData.expertise_document);

    const result = await updateExpertDetails(expertData);
    if (result.status) {
      showSuccessAlert("Registration successful! Account pending review. We'll get back to you.");
      setShowFinalSuccess(true);
      localStorage.removeItem('tempUserId');
      setTimeout(() => {
        navigate("/login");
      }, 5000);
    } else {
      handleApiError(result);
    }
  };

  const handleProduceSubmit = async () => {
    if (selectedProduce.length === 0) {
      handleError("Please select at least one produce");
      return;
    }

    const userId = localStorage.getItem('tempUserId');
    if (!userId) {
      handleError("User ID not found. Please try registering again.");
      return;
    }

    const result = await storeFarmProduce(selectedProduce, userId);
    if (result.success) {
      showSuccessAlert("Produce selection saved successfully!");
      await completeRegistrationAndLogin();
    } else {
      handleApiError(result);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isResending) return;

    setIsResending(true);
    setError(null);
    setFieldErrors({});

    try {
      const userId = localStorage.getItem('tempUserId');
      if (!userId) {
        handleError("User ID not found. Please try registering again.");
        return;
      }

      const result = await resendVerification(userId);
      if (result.status) {
        showSuccessAlert(result.message || "Verification code resent successfully!");
        setResendCooldown(60); // 60 second cooldown
      } else {
        handleError(result.message || "Failed to resend verification code");
      }
    } catch (error) {
      handleError("Failed to resend verification code");
    } finally {
      setIsResending(false);
    }
  };

  const completeRegistrationAndLogin = async () => {
    // Auto-login after successful registration - email only
    const loginData = {
      email: formData.email,
      password: formData.password
    };

    const loginResult = await login(loginData);
    if (loginResult.success) {
      // Clean up temporary user ID
      localStorage.removeItem('tempUserId');
      showSuccessAlert("Registration completed! Welcome to your farm management platform!");
      setTimeout(() => {
        const dashboardPath = getDashboardPath(loginResult.user?.role);
        navigate(dashboardPath);
      }, 2000);
    } else {
      handleError("Registration completed but auto-login failed. Please login manually.");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "userType": return "Welcome to Agricverse";
      case "basicInfo": return "Basic Information";
      case "verification": return "Verify Your Account";
      case "farmDetails": return "Farm Details";
      case "expertDetails": return "Expert Details";
      case "produce": return "Select Your Produce";
      default: return "";
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case "userType": return "Please select your user type to continue";
      case "basicInfo": return "Tell us about yourself";
      case "verification": return `We've sent a code to your email`;
      case "farmDetails": return "Tell us about your farm";
      case "expertDetails": return "Tell us about your expertise";
      case "produce": return "Choose the products you grow on your farm";
      default: return "";
    }
  };

  const getProgressSteps = () => {
    switch (userType) {
      case "Farmer":
        return ["Basic Info", "Verification", "Farm Details", "Produce"];
      case "Expert":
        return ["Basic Info", "Verification", "Expert Details"];
      case "Retailer":
        return ["Basic Info", "Verification"];
      default:
        return [];
    }
  };

  const getCurrentStepIndex = () => {
    const steps = getProgressSteps();
    switch (currentStep) {
      case "userType": return -1;
      case "basicInfo": return 0;
      case "verification": return 1;
      case "farmDetails": return 2;
      case "expertDetails": return 2;
      case "produce": return 3;
      default: return 0;
    }
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center">
      <Loader2 className="animate-spin h-4 w-4 mr-2" />
      Processing...
    </div>
  );

  const FieldError = ({ fieldName }) => {
    if (!fieldErrors[fieldName]) return null;

    return (
      <div className="field-error">
        {fieldErrors[fieldName]}
      </div>
    );
  };

  return (
    <div className="auth-container">
      <div className="auth-image">
        <img src={bgi} alt="Agriculture imagery" />
        <div className="absolute-logo">
          <img
            src="/AGRICVERSE_DARK.png"
            alt="AgricVerse Logo"
            style={{ height: 60, cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
      </div>

      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          {/* Progress Bar */}
          {currentStep !== "userType" && !showFinalSuccess && (
            <div className="progress-bar">
              {getProgressSteps().map((step, index) => (
                <div
                  key={index}
                  className={`progress-step ${index <= getCurrentStepIndex() ? "active" : ""
                    }`}
                >
                  <div className="step-number">{index + 1}</div>
                  <div className="step-label">{step}</div>
                  {index < getProgressSteps().length - 1 && (
                    <div className="step-connector"></div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Success Message Display */}
          {successMessage && (
            <div className="success-message">
              <CheckCircle size={16} />
              {successMessage}
            </div>
          )}

          {/* Error Display - Simple text instead of alert-style */}
          {error && (
            <div className="error-text">
              {error}
            </div>
          )}

          {/* User Type Selection */}
          {currentStep === "userType" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <div className="button-group">
                <button
                  onClick={() => handleUserTypeSelect("Farmer")}
                  className="primary-button"
                  disabled={isLoading}
                >
                  I am a Farmer
                  <User size={18} />
                </button>
                <button
                  onClick={() => handleUserTypeSelect("Expert")}
                  className="secondary-button"
                  disabled={isLoading}
                >
                  I am an Expert
                  <Verified size={18} />
                </button>
                <button
                  onClick={() => handleUserTypeSelect("Retailer")}
                  className="tertiary-button"
                  disabled={isLoading}
                >
                  I am a Retailer
                  <HardDriveUpload size={18} />
                </button>
              </div>
            </div>
          )}

          {/* Basic Information Form */}
          {currentStep === "basicInfo" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-rowt">
                  <div className="form-group">
                    <label htmlFor="first_name">First Name</label>
                    <div className="input-group">
                      <User size={18} />
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <FieldError fieldName="first_name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="last_name">Last Name</label>
                    <div className="input-group">
                      <User size={18} />
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                    <FieldError fieldName="last_name" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="user_name">Username</label>
                  <div className="input-group">
                    <UserCircle size={18} />
                    <input
                      type="text"
                      id="user_name"
                      name="user_name"
                      value={formData.user_name}
                      onChange={handleInputChange}
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                  <FieldError fieldName="user_name" />
                </div>

                {/* Email only - phone number login commented out */}
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-group">
                    <Mail size={18} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <FieldError fieldName="email" />
                </div>

                {/* Phone number login commented out - using email only for now */}
                {/* {usePhoneNumber ? (
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <div className="input-group">
                      <Phone size={18} />
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>
                    <FieldError fieldName="phone" />
                  </div>
                ) : null} */}

                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <div className="input-group">
                    <Lock size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <FieldError fieldName="password" />
                </div>

                <div className="form-group">
                  <label htmlFor="password_confirm">Confirm Password</label>
                  <div className="input-group">
                    <Lock size={18} />
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      id="password_confirm"
                      name="password_confirm"
                      value={formData.password_confirm}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    >
                      {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <FieldError fieldName="password_confirm" />
                </div>

                {/* Phone number toggle commented out - using email only for now */}
                {/* <div className="choice-phone">
                  <div className="span-div">
                    <p>or</p>
                  </div>
                  <button
                    type="button"
                    className="toggle-login-method"
                    onClick={() => setUsePhoneNumber(!usePhoneNumber)}
                    disabled={isLoading}
                  >
                    {usePhoneNumber ? "Use Email Instead" : "Use Phone Number"}
                  </button>
                </div> */}

                <button type="submit" className="primary-button" disabled={isLoading}>
                  {isLoading ? <LoadingSpinner /> : "Continue"}
                </button>
              </form>

              <div className="form-footer">
                <p>
                  Already have an account?{" "}
                  <button
                    onClick={() => navigate("/login")}
                    className="link-button"
                    disabled={isLoading}
                  >
                    Login
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Verification Form */}
          {currentStep === "verification" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="otp">Verification Code</label>
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    value={formData.otp}
                    onChange={(e) => {
                      // Only allow numeric input
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      if (value.length <= 6) {
                        setFormData(prev => ({ ...prev, otp: value }));
                        // Clear field error when user starts typing
                        if (fieldErrors.otp) {
                          setFieldErrors(prev => ({ ...prev, otp: null }));
                        }
                      }
                    }}
                    placeholder="Enter the 6-digit code"
                    required
                    className="verification-input"
                    maxLength={6}
                    pattern="[0-9]{6}"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <FieldError fieldName="otp" />
                </div>

                <button type="submit" className="primary-button" disabled={isLoading}>
                  {isLoading ? <LoadingSpinner /> : "Verify"}
                </button>
              </form>

              <div className="form-footer">
                <p>
                  Didn't receive the code?{" "}
                  <button
                    type="button"
                    className="link-button"
                    onClick={handleResendCode}
                    disabled={isLoading || isResending || resendCooldown > 0}
                  >
                    {isResending ? (
                      <div className="flex items-center">
                        <Loader2 className="animate-spin h-4 w-4 mr-1" />
                        Sending...
                      </div>
                    ) : resendCooldown > 0 ? (
                      `Resend in ${resendCooldown}s`
                    ) : (
                      "Resend"
                    )}
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Farm Details Form */}
          {currentStep === "farmDetails" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label htmlFor="farm_name">Farm Name</label>
                  <div className="input-group">
                    <User size={18} />
                    <input
                      type="text"
                      id="farm_name"
                      name="farm_name"
                      value={formData.farm_name}
                      onChange={handleInputChange}
                      placeholder="Enter your farm name"
                      required
                    />
                  </div>
                  <FieldError fieldName="farm_name" />
                </div>

                <div className="form-group">
                  <label htmlFor="location">Farm Location</label>
                  <div className="input-group">
                    <MapPin size={18} />
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Enter your farm location"
                      required
                    />
                  </div>
                  <FieldError fieldName="location" />
                </div>

                <button type="submit" className="primary-button" disabled={isLoading}>
                  {isLoading ? <LoadingSpinner /> : "Continue"}
                </button>
              </form>
            </div>
          )}

          {/* Expert Details Form */}
          {currentStep === "expertDetails" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="form-group">
                  <label>Areas of Expertise</label>
                  <div className="expertise-grid">
                    {[
                      "Crop Management",
                      "Soil Science",
                      "Pest Control",
                      "Irrigation",
                      "Organic Farming",
                      "Livestock Management",
                      "Agricultural Technology",
                      "Sustainable Agriculture"
                    ].map((area) => (
                      <div
                        key={area}
                        className={`expertise-item ${selectedExpertiseAreas.includes(area) ? "selected" : ""
                          }`}
                        onClick={() => {
                          handleExpertiseToggle(area);
                          setFieldErrors(prev => ({ ...prev, areas_of_expertise: null }));
                        }}
                      >
                        <span>{area}</span>
                        {selectedExpertiseAreas.includes(area) && (
                          <Check size={16} />
                        )}
                      </div>
                    ))}
                  </div>
                  <FieldError fieldName="areas_of_expertise" />
                </div>

                <div className="form-group">
                  <label htmlFor="years_of_experience">Years of Experience</label>
                  <div className="input-group">
                    <Calendar size={18} />
                    <input
                      type="number"
                      id="years_of_experience"
                      name="years_of_experience"
                      value={formData.years_of_experience}
                      onChange={handleInputChange}
                      placeholder="Enter years of experience"
                      min="1"
                      required
                    />
                  </div>
                  <FieldError fieldName="years_of_experience" />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="extension_agent"
                      checked={formData.extension_agent}
                      onChange={(e) => handleInputChange({ target: { name: 'extension_agent', value: e.target.checked } })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>I am an Extension Agent</span>
                  </label>
                </div>

                <div className="form-group">
                  <label htmlFor="expertise_document">Document of Expertise <span style={{ color: 'red' }}>*</span></label>
                  <div className="input-group">
                    <HardDriveUpload size={18} />
                    <input
                      type="file"
                      id="expertise_document"
                      name="expertise_document"
                      onChange={handleInputChange}
                      accept=".pdf,.doc,.docx"
                      required
                    />
                  </div>
                  <small className="file-hint">
                    Upload your certification (PDF or Word, max 10MB)
                  </small>
                  <FieldError fieldName="expertise_document" />
                </div>

                <button type="submit" className="primary-button" disabled={isLoading}>
                  {isLoading ? <LoadingSpinner /> : "Complete Registration"}
                </button>
              </form>
            </div>
          )}

          {/* Produce Selection Form */}
          {currentStep === "produce" && !showFinalSuccess && (
            <div className="form-section active">
              <h1 className="form-title">{getStepTitle()}</h1>
              <p className="form-subtitle">{getStepSubtitle()}</p>

              <form onSubmit={handleSubmit} className="auth-form">
                <div className="product-grid">
                  {produceList.map((produce) => (
                    <div
                      key={produce.id}
                      className={`product-card ${selectedProduce.includes(produce.id) ? "selected" : ""
                        }`}
                      onClick={() => handleProduceToggle(produce.id)}
                    >
                      <div className="product-image-container">
                        <img
                          src={produce.image}
                          alt={produce.name}
                          className="product-image"
                        />
                        {selectedProduce.includes(produce.id) && (
                          <div className="product-checkmark">
                            <Check size={20} color="white" />
                          </div>
                        )}
                      </div>
                      <h3 className="product-name">{produce.name}</h3>
                    </div>
                  ))}
                </div>

                <button
                  type="submit"
                  className="primary-button"
                  disabled={selectedProduce.length === 0 || isLoading}
                >
                  {isLoading ? <LoadingSpinner /> : "Complete Registration"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}